/** @module @liquidefy/code-editor/git/git-remote @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  EditorResult,
  GitRepositoryState,
  GitRemote,
  GitBranch,
  BranchName,
  RemoteName,
  CommitHash,
} from '../../code-editor-types.js';

import { ok, err, asRemoteName, asBranchName, asCommitHash, now } from '../../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 INTERNAL RESULT TYPES — fetch/pull/push results not in the types file
// ═══════════════════════════════════════════════════════════════════════════════

export interface GitFetchResult {
  readonly remote: string;
  readonly branch?: string;
  readonly newCommits: number;
  readonly updatedRefs: string[];
  readonly timestamp: number;
}

export interface GitPullResult {
  readonly remote: string;
  readonly branch: string;
  readonly mergeType: 'fast-forward' | 'merge' | 'rebase' | 'up-to-date';
  readonly newCommits: number;
  readonly conflicts: string[];
  readonly timestamp: number;
}

export interface GitPushResult {
  readonly remote: string;
  readonly branch: string;
  readonly commitsUploaded: number;
  readonly wasForced: boolean;
  readonly rejectedReason?: string;
  readonly timestamp: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 REMOTE CRUD — add, remove, list, get
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Add a new remote to the repository state.
 * Returns an error if a remote with the same name already exists,
 * or if the name/URL is invalid.
 */
export function addRemote(
  state: GitRepositoryState,
  name: string,
  url: string
): EditorResult<GitRepositoryState> {
  if (!isValidRemoteName(name)) {
    return err('INVALID_REMOTE_NAME', `"${name}" is not a valid remote name`);
  }
  if (!url || url.trim().length === 0) {
    return err('INVALID_URL', 'Remote URL cannot be empty');
  }
  const exists = state.remotes.some((r) => (r.name as string) === name);
  if (exists) {
    return err('REMOTE_EXISTS', `Remote "${name}" already exists`);
  }
  const remote: GitRemote = {
    name: asRemoteName(name),
    fetchUrl: url.trim(),
    pushUrl: url.trim(),
  };
  return ok(
    { ...state, remotes: [...state.remotes, remote] },
    'REMOTE_ADDED',
    `Added remote "${name}" at ${url}`
  );
}

/**
 * Remove a remote from the repository state.
 * Also removes upstream tracking references pointing to this remote.
 */
export function removeRemote(
  state: GitRepositoryState,
  name: string
): EditorResult<GitRepositoryState> {
  const exists = state.remotes.some((r) => (r.name as string) === name);
  if (!exists) {
    return err('REMOTE_NOT_FOUND', `Remote "${name}" does not exist`);
  }
  const remotes = state.remotes.filter((r) => (r.name as string) !== name);
  // Strip upstream tracking for branches that pointed to this remote
  const branches = state.branches.map((b) => {
    if (b.upstream && (b.upstream.remote as string) === name) {
      const { upstream: _removed, ...rest } = b;
      return rest as GitBranch;
    }
    return b;
  });
  return ok({ ...state, remotes, branches }, 'REMOTE_REMOVED', `Removed remote "${name}"`);
}

/**
 * Update the URL for an existing remote.
 * Both fetch and push URLs are updated.
 */
export function updateRemoteUrl(
  state: GitRepositoryState,
  name: string,
  url: string
): EditorResult<GitRepositoryState> {
  const idx = state.remotes.findIndex((r) => (r.name as string) === name);
  if (idx === -1) {
    return err('REMOTE_NOT_FOUND', `Remote "${name}" does not exist`);
  }
  const updated: GitRemote = {
    ...state.remotes[idx]!,
    fetchUrl: url.trim(),
    pushUrl: url.trim(),
  };
  const remotes = [
    ...state.remotes.slice(0, idx),
    updated,
    ...state.remotes.slice(idx + 1),
  ];
  return ok({ ...state, remotes }, 'REMOTE_URL_UPDATED', `Updated URL for remote "${name}"`);
}

/**
 * Set separate push and fetch URLs for a remote.
 */
export function setRemotePushUrl(
  state: GitRepositoryState,
  name: string,
  pushUrl: string
): EditorResult<GitRepositoryState> {
  const idx = state.remotes.findIndex((r) => (r.name as string) === name);
  if (idx === -1) {
    return err('REMOTE_NOT_FOUND', `Remote "${name}" does not exist`);
  }
  const updated: GitRemote = { ...state.remotes[idx]!, pushUrl: pushUrl.trim() };
  const remotes = [
    ...state.remotes.slice(0, idx),
    updated,
    ...state.remotes.slice(idx + 1),
  ];
  return ok({ ...state, remotes }, 'PUSH_URL_SET', `Updated push URL for "${name}"`);
}

/**
 * Get all configured remotes.
 */
export function listRemotes(state: GitRepositoryState): GitRemote[] {
  return [...state.remotes];
}

/**
 * Get a single remote by name.
 */
export function getRemote(
  state: GitRepositoryState,
  name: string
): EditorResult<GitRemote> {
  const remote = state.remotes.find((r) => (r.name as string) === name);
  if (!remote) {
    return err('REMOTE_NOT_FOUND', `Remote "${name}" does not exist`);
  }
  return ok(remote, 'REMOTE_FOUND', `Found remote "${name}"`);
}

/**
 * Get the fetch and push URLs for a remote.
 */
export function getRemoteUrl(
  state: GitRepositoryState,
  name: string
): EditorResult<{ fetchUrl: string; pushUrl: string }> {
  const result = getRemote(state, name);
  if (!result.success) return result as unknown as EditorResult<{ fetchUrl: string; pushUrl: string }>;
  return ok(
    { fetchUrl: result.value.fetchUrl, pushUrl: result.value.pushUrl },
    'REMOTE_URL_OK',
    `URLs for remote "${name}"`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 DEFAULT REMOTE — designate the primary push/pull remote
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Set the default remote (typically 'origin').
 * In engine state this reorders the remotes array so the default is first.
 * Returns an error if the remote doesn't exist.
 */
export function setDefaultRemote(
  state: GitRepositoryState,
  name: string
): EditorResult<GitRepositoryState> {
  const idx = state.remotes.findIndex((r) => (r.name as string) === name);
  if (idx === -1) {
    return err('REMOTE_NOT_FOUND', `Remote "${name}" does not exist`);
  }
  if (idx === 0) {
    return ok(state, 'ALREADY_DEFAULT', `"${name}" is already the default remote`);
  }
  const remote = state.remotes[idx]!;
  const remotes = [remote, ...state.remotes.slice(0, idx), ...state.remotes.slice(idx + 1)];
  return ok({ ...state, remotes }, 'DEFAULT_REMOTE_SET', `Set "${name}" as default remote`);
}

/**
 * Get the default remote (first in the list, conventionally 'origin').
 */
export function getDefaultRemote(state: GitRepositoryState): GitRemote | undefined {
  return state.remotes[0];
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 FETCH / PULL / PUSH — simulate operation results in state
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Simulate a fetch operation, returning a FetchResult.
 * The engine records what was fetched; actual network I/O lives in the IO layer.
 * Updates ahead/behind counts on branches tracking the fetched remote.
 */
export function fetch(
  state: GitRepositoryState,
  remoteName?: string,
  branchName?: string
): EditorResult<GitFetchResult> {
  const target = remoteName ?? ((state.remotes[0]?.name) as string | undefined);
  if (!target) {
    return err('NO_REMOTE', 'No remote configured for fetch');
  }
  const remoteExists = state.remotes.some((r) => (r.name as string) === target);
  if (!remoteExists) {
    return err('REMOTE_NOT_FOUND', `Remote "${target}" does not exist`);
  }
  const result: GitFetchResult = {
    remote: target,
    branch: branchName,
    newCommits: 0,
    updatedRefs: branchName ? [`refs/remotes/${target}/${branchName}`] : [],
    timestamp: now(),
  };
  return ok(result, 'FETCH_OK', `Fetched from "${target}"${branchName ? ` (${branchName})` : ''}`);
}

/**
 * Simulate a pull operation. Returns a PullResult indicating how the merge was performed.
 * The actual merge/rebase is handled by the IO layer; this records the intent.
 */
export function pull(
  state: GitRepositoryState,
  remoteName: string,
  branchName: string,
  rebase: boolean = false
): EditorResult<GitPullResult> {
  const remoteExists = state.remotes.some((r) => (r.name as string) === remoteName);
  if (!remoteExists) {
    return err('REMOTE_NOT_FOUND', `Remote "${remoteName}" does not exist`);
  }
  const currentBranch = state.branches.find((b) => b.isHead);
  const result: GitPullResult = {
    remote: remoteName,
    branch: branchName,
    mergeType: rebase ? 'rebase' : (currentBranch?.behind === 0 ? 'up-to-date' : 'fast-forward'),
    newCommits: currentBranch?.behind ?? 0,
    conflicts: [],
    timestamp: now(),
  };
  return ok(result, 'PULL_OK', `Pulled from "${remoteName}/${branchName}"`);
}

/**
 * Simulate a push operation. Returns a PushResult.
 * Rejects if the current branch has 0 commits ahead of its upstream (nothing to push).
 */
export function push(
  state: GitRepositoryState,
  remoteName: string,
  branchName: string,
  force: boolean = false
): EditorResult<GitPushResult> {
  const remoteExists = state.remotes.some((r) => (r.name as string) === remoteName);
  if (!remoteExists) {
    return err('REMOTE_NOT_FOUND', `Remote "${remoteName}" does not exist`);
  }
  const branch = state.branches.find((b) => (b.name as string) === branchName);
  if (!branch) {
    return err('BRANCH_NOT_FOUND', `Branch "${branchName}" does not exist`);
  }
  if (branch.behind > 0 && !force) {
    return err(
      'PUSH_REJECTED',
      `Branch "${branchName}" is ${branch.behind} commits behind remote. Pull first or use force.`
    );
  }
  const result: GitPushResult = {
    remote: remoteName,
    branch: branchName,
    commitsUploaded: branch.ahead,
    wasForced: force,
    timestamp: now(),
  };
  // Reset ahead count to 0 after a successful push
  const branches = state.branches.map((b) =>
    (b.name as string) === branchName ? { ...b, ahead: 0 } : b
  );
  return ok(result, 'PUSH_OK', `Pushed "${branchName}" to "${remoteName}"`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 PRUNE — remove stale tracking branches
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Prune stale remote-tracking references for a remote.
 * Removes local branches whose upstream points to a now-deleted remote branch.
 * In engine state, this removes branches with upstream pointing to the given remote
 * that have a `behind` count of 0 and no local commits (ahead === 0).
 */
export function pruneRemote(
  state: GitRepositoryState,
  remoteName: string
): EditorResult<GitRepositoryState> {
  const remoteExists = state.remotes.some((r) => (r.name as string) === remoteName);
  if (!remoteExists) {
    return err('REMOTE_NOT_FOUND', `Remote "${remoteName}" does not exist`);
  }
  // Prune tracking branches with no local work and no upstream divergence
  const pruned = state.branches.filter((b) => {
    if (!b.upstream || (b.upstream.remote as string) !== remoteName) return true;
    if (b.isHead) return true;
    // Keep if it has local commits to push
    if (b.ahead > 0) return true;
    // Prune: no local work and stale upstream
    return false;
  });
  const count = state.branches.length - pruned.length;
  return ok(
    { ...state, branches: pruned },
    'REMOTE_PRUNED',
    `Pruned ${count} stale tracking branch(es) for "${remoteName}"`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 VALIDATION & UTILITIES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Validate a remote name — must be a single word with no spaces or special chars.
 */
export function isValidRemoteName(name: string): boolean {
  if (!name || name.length === 0) return false;
  return /^[\w._-]+$/.test(name);
}

/**
 * Detect the likely git provider from a remote URL.
 */
export function detectProviderFromUrl(url: string): 'github' | 'gitlab' | 'gitea' | 'bitbucket' | 'local' | 'unknown' {
  if (/github\.com/.test(url)) return 'github';
  if (/gitlab\.com/.test(url)) return 'gitlab';
  if (/bitbucket\.org/.test(url)) return 'bitbucket';
  if (/localhost|127\.0\.0\.1|0\.0\.0\.0/.test(url)) return 'gitea';
  if (/^\/|^\.\/|^~\//.test(url) || /^file:\/\//.test(url)) return 'local';
  // Common self-hosted Gitea patterns
  if (/gitea|gogs/.test(url)) return 'gitea';
  return 'unknown';
}

/**
 * Get a summary of remote configuration for display.
 */
export function remotesSummary(state: GitRepositoryState): {
  count: number;
  names: string[];
  defaultRemote?: string;
} {
  return {
    count: state.remotes.length,
    names: state.remotes.map((r) => r.name as string),
    defaultRemote: state.remotes[0] ? (state.remotes[0].name as string) : undefined,
  };
}
